/* eslint-disable react-hooks/exhaustive-deps */

import { FC, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useOffer } from "../../hooks/useOffer";
import { useTypedSelector } from "../../hooks/useTypedSelector";
import { ERROR_MESSAGES } from "../../messages/errorMessages";
import { INFO_MESSAGES } from "../../messages/infoMessages";
import { IOfferState } from "../../model/IOffer";
import { IPart } from "../../model/IPart";
import { getErrorFromCatch } from "../../services/api";
import { offerApi } from "../../services/offer.api";
import { getCurrentUser } from "../../store/authSlice";
import { IOfferCustomer, getOfferCustomerParams } from "../../store/offerSlice";
import { IZodErrorsState } from "../../utils/checkFormStateByZod";
import { TInputEventHandle } from "../form/Input";
import OfferFormTpl from "./OfferFormTpl";

const OfferForm: FC<{
  customer: IOfferCustomer;
  part: IPart;
  unsetPart: () => unknown;
}> = ({ customer, part, unsetPart }) => {
  const offerCustomerState = useTypedSelector(getOfferCustomerParams());
  const { sendOffer } = useOffer();

  // -- Offer state
  const [isPending, setIsPending] = useState<boolean>(() => false);
  const [isSuccess, setIsSuccess] = useState<boolean>(() => false);
  const [offerErrState, setOfferErrState] =
    useState<Partial<IZodErrorsState<IOfferState>>>();
  const [offerState, setOfferState] = useState<IOfferState>(() => ({
    productId: part?.id || 0,
    email: customer?.email || "",
    customerName: customer?.name || "",
    zip: customer?.zip,
    name: customer?.name,
    website: customer?.website,
    company: customer?.company,
    state: customer?.state,
    city: customer?.city,
    address: customer?.address,
    phone: customer?.phone,
    subject: "",
    comments: "",
    discountId: 0,
    sendEmail: true,
  }));

  // -- Get offer price information
  const offerPriceInfo = offerApi.useGetOfferPriceInfoQuery(
    { partNumber: part?.product?.partNumber || "", zip: offerState.zip },
    { skip: !offerState.zip }
  );

  useEffect(() => {
    offerPriceInfo?.isError &&
      toast.error(getErrorFromCatch(offerPriceInfo.error) || "");
  }, [offerPriceInfo.requestId]);

  if (!customer?.email) {
    toast.error(ERROR_MESSAGES["offer-no-email"]);
    unsetPart();
    return null;
  }
  if (!part?.id) {
    toast.error(ERROR_MESSAGES["offer-no-product-id"]);
    return null;
  }

  // -- Methods
  const handleClose = unsetPart;

  const handleField =
    (key: keyof IOfferState): TInputEventHandle =>
    (e) =>
      setOfferState((prevState) => ({
        ...prevState,
        [key]: e?.target?.value || "",
      }));

  const onSendOffer = () => {
    setIsPending(() => true);

    (async () => {
      const result = await sendOffer(offerState);

      setIsPending(() => false);
      if (!result) {
        toast.success(INFO_MESSAGES["offer-create"]);
        return setIsSuccess(() => true);
      }

      return typeof result === "string"
        ? toast.error(`${ERROR_MESSAGES["offer-create-failed"]} ${result}`)
        : setOfferErrState(() => result);
    })();
  };

  return (
    <OfferFormTpl
      // disabledFields={disabledFields}
      offerState={offerState}
      setOfferState={setOfferState}
      offerErrState={offerErrState}
      part={part}
      isSuccess={isSuccess}
      handleClose={handleClose}
      handleField={handleField}
      isPending={isPending}
      // offerPriceInfo={offerPriceInfo?.data}
      offerPriceInfo={{
        partPrice: Number(part?.product?.price) || undefined,
        shipping:
          offerPriceInfo?.data?.shipping ||
          Number(part?.product?.shipping) ||
          undefined,
        tax:
          offerState?.zip && offerPriceInfo?.data?.tax
            ? offerPriceInfo?.data?.tax
            : undefined,
        total:
          offerState?.zip && offerPriceInfo?.data?.total
            ? offerPriceInfo?.data?.total
            : undefined,
      }}
      offerCustomerState={offerCustomerState}
      onSendOffer={onSendOffer}
    />
  );
};

export default OfferForm;
